import React, {useEffect, useMemo, useRef, useState} from 'react';
import {CButton, CInput, CSelect, CTextarea} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import {getApi} from "../../apis/backendApis";
import useAlert from "../../services/useAlert";
import {
    Anexo,
    CATEGORIAS_ANEXO,
    ContatoCliente,
    Historico,
    PapelProcesso,
    Processo,
    SituacaoProcesso,
    TIPOS_HISTORICO,
    TIPO_PAPEL_CLIENTE,
    dataBr,
    tamanhoArquivo,
} from "./modelo";
import {Executar} from "./processoTela";
import {opcoesDePessoas, textoSetores, usePessoasPapel} from "./pessoasPapel";

/** Download pela API (o token vai na query do axios), salvo pelo navegador. */
export const baixarAnexo = (anexo: Anexo) => {
    getApi().get(`processo/anexo/download/${anexo.id}`, {responseType: 'blob'}).then(resposta => {
        const url = URL.createObjectURL(resposta.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = anexo.nome;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }).catch(() => window.alert('Não foi possível baixar o arquivo.'));
};

// ---------------------------------------------------------------- responsaveis

interface ResponsaveisProps {
    processo: Processo,
    usuarios: Array<{id: number, nome: string}>,
    executar: Executar,
    /** Recarrega o processo (os contatos do cliente vao junto para os agendamentos). */
    recarregar: () => void,
}

const ORIGEM_CONTATO: {[origem: string]: string} = {
    manual: 'cadastro',
    papel: 'contato de papel',
    evento: 'participante de evento',
    portal: 'agendou pelo portal',
};

/**
 * Responsaveis pelo cliente (#484909): os contatos do cliente (por cliente, valem
 * para todos os processos dele), com os papeis e eventos de cada um neste processo.
 * Entram sozinhos ao participar de evento, virar contato de papel ou agendar pelo
 * portal; aqui tambem se cadastra, altera e tira da lista.
 */
const ContatosClienteAba = ({processo, recarregar}: {processo: Processo, recarregar: () => void}) => {
    const [showAlert] = useAlert();
    const [contatos, setContatos] = useState<ContatoCliente[] | null>(null);
    const [form, setForm] = useState<{id?: number, nome: string, email: string, telefone: string, cargo: string} | null>(null);
    const ativo = processo.situacao === SituacaoProcesso.ABERTO || processo.situacao === SituacaoProcesso.EXECUTANDO;

    useEffect(() => {
        getApi().get(`processo/contatos-cliente/${processo.id}`).then(r => setContatos(r.data)).catch(() => setContatos([]));
    }, [processo.id]);

    const envia = (dados: any) => {
        const corpo = new FormData();
        corpo.append('data', JSON.stringify(dados));
        return getApi().post(`processo/contato-cliente/${processo.id}`, corpo)
            .then(r => {
                showAlert({msg: r.data.msg, type: 'success', toaster: true});
                setContatos(r.data.contatos);
                setForm(null);
                recarregar();
            })
            .catch(e => showAlert({title: 'Não foi possível', msg: String(e?.response?.data?.error ?? 'Erro ao salvar o contato.'), type: 'danger', toaster: true}));
    };

    const remove = (contato: ContatoCliente) => {
        if (window.confirm(`Tirar ${contato.nome} da lista de contatos do cliente? Os eventos e papéis não mudam.`)) envia({id: contato.id, remover: true});
    };

    const muda = (campo: string) => (e: any) => setForm(atual => (atual ? {...atual, [campo]: e.target.value} : atual));

    return (
        <section className="pr-contatos" aria-label="Responsáveis pelo cliente">
            <div className="pr-contatos-topo">
                <p className="pr-ajuda">
                    Quem do <strong>{processo.cliente.nome}</strong> participa da implantação. Valem para todos os processos do cliente e
                    aparecem para escolher nos agendamentos. Entram sozinhos ao participar de um evento, virar contato de papel ou agendar pelo portal.
                </p>
                {ativo && !form && (
                    <CButton color="primary" size="sm" onClick={() => setForm({nome: '', email: '', telefone: '', cargo: ''})}>
                        <CIcon name="cil-plus"/> Novo contato
                    </CButton>
                )}
            </div>
            {form && (
                <div className="pr-contatos-form">
                    <CInput placeholder="Nome *" value={form.nome} onChange={muda('nome')} aria-label="Nome"/>
                    <CInput type="email" placeholder="E-mail" value={form.email} onChange={muda('email')} aria-label="E-mail"/>
                    <CInput placeholder="Telefone" value={form.telefone} onChange={muda('telefone')} aria-label="Telefone"/>
                    <CInput placeholder="Cargo" value={form.cargo} onChange={muda('cargo')} aria-label="Cargo"/>
                    <div className="pr-contatos-form-acoes">
                        <CButton color="secondary" variant="outline" size="sm" onClick={() => setForm(null)}>Cancelar</CButton>
                        <CButton color="primary" size="sm" disabled={!form.nome.trim()} onClick={() => envia(form)}>{form.id ? 'Salvar' : 'Cadastrar'}</CButton>
                    </div>
                </div>
            )}
            {contatos === null && <div className="pr-vazio">Carregando…</div>}
            {contatos !== null && contatos.length === 0 && <div className="pr-vazio">Nenhum contato do cliente ainda.</div>}
            {contatos !== null && contatos.length > 0 && (
                <div className="pr-contatos-grade" role="table">
                    <div className="pr-contatos-linha cabecalho" role="row">
                        <span>Nome</span><span>E-mail</span><span>Telefone</span><span>Cargo</span><span>Neste processo</span><span/>
                    </div>
                    {contatos.map(contato => (
                        <div key={contato.id} className="pr-contatos-linha" role="row">
                            <span><strong>{contato.nome}</strong><small>{ORIGEM_CONTATO[contato.origem || ''] || ''}</small></span>
                            <span>{contato.email ? <a href={`mailto:${contato.email}`}>{contato.email}</a> : '—'}</span>
                            <span>{contato.telefone || '—'}</span>
                            <span>{contato.cargo || '—'}</span>
                            <span className="pr-contatos-uso">
                                {(contato.papeis || []).map(p => <span key={p} className="pr-chip cliente">{p}</span>)}
                                {(contato.eventos || 0) > 0 && <span className="pr-chip">{contato.eventos} {contato.eventos === 1 ? 'evento' : 'eventos'}</span>}
                                {contato.proximo_evento && <small>próximo: {contato.proximo_evento.titulo} · {dataBr(contato.proximo_evento.inicio)}</small>}
                                {!(contato.papeis || []).length && !contato.eventos && <small>—</small>}
                            </span>
                            <span className="pr-contatos-acoes">
                                {ativo && (
                                    <>
                                        <button type="button" className="pr-link" onClick={() => setForm({id: contato.id, nome: contato.nome, email: contato.email || '',
                                            telefone: contato.telefone || '', cargo: contato.cargo || ''})}>alterar</button>
                                        <button type="button" className="pr-link perigo" onClick={() => remove(contato)}>remover</button>
                                    </>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};

/** Responsaveis: a equipe (papeis do processo) e os contatos do cliente (#484909). */
export const ResponsaveisAba = (props: ResponsaveisProps) => {
    const [visao, setVisao] = useState<'equipe' | 'cliente'>('equipe');
    return (
        <div>
            <div className="pr-presets pr-responsaveis-visao" role="tablist">
                <button type="button" role="tab" aria-selected={visao === 'equipe'} className={visao === 'equipe' ? 'ativo' : ''} onClick={() => setVisao('equipe')}>Equipe e papéis</button>
                <button type="button" role="tab" aria-selected={visao === 'cliente'} className={visao === 'cliente' ? 'ativo' : ''} onClick={() => setVisao('cliente')}>
                    Responsáveis pelo cliente <span>{(props.processo.contatos_cliente || []).length}</span>
                </button>
            </div>
            {visao === 'equipe' ? <PapeisResponsaveis {...props}/> : <ContatosClienteAba processo={props.processo} recarregar={props.recarregar}/>}
        </div>
    );
};

const PapeisResponsaveis = ({processo, usuarios, executar}: ResponsaveisProps) => {
    // So os usuarios dos setores do fluxo ocupam os papeis (#484909); a pessoa atual sempre aparece.
    const pessoasPapel = usePessoasPapel({processo_id: processo.id});
    const opcoes = opcoesDePessoas(pessoasPapel, usuarios);
    const [editando, setEditando] = useState<number | null>(null);
    const [form, setForm] = useState<any>({});
    const ativo = processo.situacao === SituacaoProcesso.ABERTO || processo.situacao === SituacaoProcesso.EXECUTANDO;

    const abre = (papel: PapelProcesso) => {
        setEditando(papel.id);
        setForm({
            usuario_id: papel.usuario?.id ?? '',
            contato_nome: papel.contato?.nome ?? '',
            contato_email: papel.contato?.email ?? '',
            contato_telefone: papel.contato?.telefone ?? '',
            contato_cargo: papel.contato?.cargo ?? '',
            motivo: '',
        });
    };

    const salva = (papel: PapelProcesso) => {
        executar(`processo/papel/pessoa/${papel.id}`, {...form, usuario_id: form.usuario_id || null}).then(ok => ok && setEditando(null));
    };

    const muda = (campo: string) => (e: any) => setForm({...form, [campo]: e.target.value});

    return (
        <div className="pr-responsaveis">
            {processo.papeis.map(papel => (
                <article key={papel.id} className="pr-responsavel">
                    <div className="pr-responsavel-topo">
                        <div>
                            <strong>{papel.pessoa || 'Sem responsável'}</strong>
                            <span>{papel.nome} · {papel.codigo}{papel.tipo === TIPO_PAPEL_CLIENTE ? ' · cliente' : ''}</span>
                            {papel.contato?.email && <span>{papel.contato.email}{papel.contato.telefone ? ` · ${papel.contato.telefone}` : ''}</span>}
                        </div>
                        <span className="pr-responsavel-pct">{Math.round(papel.percentual)}%</span>
                    </div>
                    <div className="pr-barra fina"><span style={{width: `${papel.percentual}%`}}/></div>
                    <div className="pr-responsavel-numeros">
                        <span>{papel.resolvidos}/{papel.itens} itens</span>
                        {papel.atrasados > 0 && <span className="pr-chip atrasado">{papel.atrasados} atrasados</span>}
                        {processo.pode_gerir && ativo && editando !== papel.id && (
                            <button type="button" className="pr-link" onClick={() => abre(papel)}>Trocar pessoa</button>
                        )}
                    </div>
                    {editando === papel.id && (
                        <div className="pr-responsavel-form">
                            <CSelect value={form.usuario_id} onChange={muda('usuario_id')}>
                                <option value="">{papel.tipo === TIPO_PAPEL_CLIENTE ? 'Sem usuário (só contato)' : 'Sem responsável'}</option>
                                {papel.usuario && !opcoes.some(u => u.id === papel.usuario!.id) && <option value={papel.usuario.id}>{papel.usuario.nome}</option>}
                                {opcoes.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                            </CSelect>
                            {pessoasPapel?.restrito && <p className="pr-ajuda pr-setores-fluxo">{textoSetores(pessoasPapel)}</p>}
                            {papel.tipo === TIPO_PAPEL_CLIENTE && (
                                <>
                                    {(processo.contatos_cliente || []).length > 0 && (
                                        <CSelect value="" aria-label="Escolher dos contatos do cliente" onChange={(e: any) => {
                                            const contato = processo.contatos_cliente.find(c => String(c.id) === e.target.value);
                                            if (contato) setForm({...form, contato_nome: contato.nome, contato_email: contato.email || '',
                                                contato_telefone: contato.telefone || '', contato_cargo: contato.cargo || ''});
                                        }}>
                                            <option value="">Escolher dos contatos do cliente…</option>
                                            {processo.contatos_cliente.map(c => <option key={c.id} value={c.id}>{c.nome}{c.email ? ` · ${c.email}` : ''}</option>)}
                                        </CSelect>
                                    )}
                                    <CInput placeholder="Nome do contato" value={form.contato_nome} onChange={muda('contato_nome')}/>
                                    <CInput type="email" placeholder="E-mail" value={form.contato_email} onChange={muda('contato_email')}/>
                                    <CInput placeholder="Telefone" value={form.contato_telefone} onChange={muda('contato_telefone')}/>
                                    <CInput placeholder="Cargo" value={form.contato_cargo} onChange={muda('contato_cargo')}/>
                                </>
                            )}
                            <CTextarea rows={2} placeholder="Motivo (obrigatório)" value={form.motivo} onChange={muda('motivo')}/>
                            <div className="pr-acoes-modo">
                                <CButton color="secondary" variant="outline" onClick={() => setEditando(null)}>Cancelar</CButton>
                                <CButton color="primary" disabled={!form.motivo.trim()} onClick={() => salva(papel)}>Salvar</CButton>
                            </div>
                        </div>
                    )}
                </article>
            ))}
        </div>
    );
};

// ---------------------------------------------------------------- historico

interface HistoricoProps {
    processoId: number,
    versao: number,
    podeComentar: boolean,
    executar: Executar,
}

export const HistoricoAba = ({processoId, versao, podeComentar, executar}: HistoricoProps) => {
    const [linhas, setLinhas] = useState<Historico[] | null>(null);
    const [tipo, setTipo] = useState<string>('');
    const [comentario, setComentario] = useState('');
    const [visivel, setVisivel] = useState(false);

    useEffect(() => {
        getApi().get(`processo/historico/${processoId}`).then(r => setLinhas(r.data)).catch(() => setLinhas([]));
    }, [processoId, versao]);

    const tipos = useMemo(() => Array.from(new Set((linhas || []).map(l => l.tipo))), [linhas]);
    const visiveis = (linhas || []).filter(l => !tipo || l.tipo === tipo);

    const comentar = () => {
        executar(`processo/comentario/${processoId}`, {mensagem: comentario, visivel_cliente: visivel}).then(ok => ok && setComentario(''));
    };

    return (
        <div className="pr-historico">
            {podeComentar && (
                <div className="pr-bloco">
                    <CTextarea rows={2} value={comentario} onChange={(e: any) => setComentario(e.target.value)} placeholder="Comentário sobre o processo"/>
                    <div className="pr-comentar">
                        <label className="pr-opcao pequena"><input type="checkbox" checked={visivel} onChange={e => setVisivel(e.target.checked)}/> visível ao cliente</label>
                        <CButton size="sm" color="primary" variant="outline" disabled={!comentario.trim()} onClick={comentar}>Comentar</CButton>
                    </div>
                </div>
            )}
            <div className="pr-filtros">
                <button type="button" className={!tipo ? 'ativo' : ''} onClick={() => setTipo('')}>Tudo <span>{linhas?.length ?? 0}</span></button>
                {tipos.map(t => (
                    <button type="button" key={t} className={tipo === t ? 'ativo' : ''} onClick={() => setTipo(t)}>
                        {TIPOS_HISTORICO[t] || t} <span>{(linhas || []).filter(l => l.tipo === t).length}</span>
                    </button>
                ))}
            </div>
            {linhas === null && <div className="pr-vazio">Carregando…</div>}
            <ol className="pr-linha-tempo grande">
                {visiveis.map(h => (
                    <li key={h.id} className={`tipo-${h.tipo.toLowerCase()}`}>
                        <span className="pr-linha-quando">{dataBr(h.data_hora)} · {TIPOS_HISTORICO[h.tipo] || h.tipo}{h.visivel_cliente ? ' · visível ao cliente' : ''}</span>
                        <span>{h.tipo === 'COMENTARIO' && h.dados?.mensagem ? `${h.usuario}: ${h.dados.mensagem}` : h.descricao}</span>
                        {h.dados?.motivo && h.tipo !== 'COMENTARIO' && !h.descricao.includes(h.dados.motivo) && <em>Motivo: {h.dados.motivo}</em>}
                    </li>
                ))}
            </ol>
        </div>
    );
};

// ---------------------------------------------------------------- documentos

interface DocumentosProps {
    processo: Processo,
    versao: number,
    executar: Executar,
    recarregar: () => void,
}

export const DocumentosAba = ({processo, versao, executar, recarregar}: DocumentosProps) => {
    const [anexos, setAnexos] = useState<Anexo[] | null>(null);
    const [busca, setBusca] = useState('');
    const [categoria, setCategoria] = useState<number>(7);
    const [visivel, setVisivel] = useState(true);
    const arquivoRef = useRef<HTMLInputElement>(null);
    const ativo = processo.situacao === SituacaoProcesso.ABERTO || processo.situacao === SituacaoProcesso.EXECUTANDO;

    useEffect(() => {
        getApi().get(`processo/anexos/${processo.id}`).then(r => setAnexos(r.data)).catch(() => setAnexos([]));
    }, [processo.id, versao]);

    const onde = useMemo(() => {
        const mapa = new Map<number, string>();
        processo.etapas.forEach(etapa => etapa.itens.forEach(item => mapa.set(item.id, `${etapa.nome} › ${item.titulo}`)));
        return mapa;
    }, [processo]);
    const nomeEtapa = (id: number | null) => processo.etapas.find(e => e.id === id)?.nome;

    const termo = busca.trim().toLowerCase();
    const filtrados = (anexos || []).filter(a => !termo || a.nome.toLowerCase().includes(termo));
    const porCategoria = CATEGORIAS_ANEXO.getList()
        .map((c: any) => ({categoria: c, anexos: filtrados.filter(a => a.categoria === c.value)}))
        .filter(grupo => grupo.anexos.length > 0);

    const enviar = (lista: FileList | null) => {
        if (!lista || lista.length === 0) return;
        executar(`processo/anexo/${processo.id}`, {categoria, visivel_cliente: visivel}, Array.from(lista))
            .then(() => { if (arquivoRef.current) arquivoRef.current.value = ''; });
    };

    const remover = (anexo: Anexo) => {
        if (!window.confirm(`Remover ${anexo.nome}?`)) return;
        getApi().delete(`processo/anexo/${anexo.id}`).then(recarregar)
            .catch(e => window.alert(e?.response?.data?.error ?? 'Não foi possível remover.'));
    };

    return (
        <div className="pr-documentos">
            {ativo && (
                <div className="pr-bloco pr-enviar-doc">
                    <CSelect value={categoria} onChange={(e: any) => setCategoria(Number(e.target.value))}>
                        {CATEGORIAS_ANEXO.getList().map((c: any) => <option key={c.value} value={c.value}>{c.description}</option>)}
                    </CSelect>
                    <label className="pr-opcao pequena"><input type="checkbox" checked={visivel} onChange={e => setVisivel(e.target.checked)}/> visível ao cliente</label>
                    <label className="pr-enviar">
                        <CIcon name="cil-paperclip"/> Enviar documento
                        <input ref={arquivoRef} type="file" multiple onChange={e => enviar(e.target.files)}/>
                    </label>
                </div>
            )}
            <div className="pr-busca">
                <CIcon name="cil-search"/>
                <CInput placeholder="Buscar documento" value={busca} onChange={(e: any) => setBusca(e.target.value)}/>
            </div>
            {anexos === null && <div className="pr-vazio">Carregando…</div>}
            {anexos !== null && filtrados.length === 0 && <div className="pr-vazio">Nenhum documento.</div>}
            {porCategoria.map(grupo => (
                <section key={grupo.categoria.value} className="pr-doc-grupo">
                    <div className="pr-grupo-nome">{grupo.categoria.description} · {grupo.anexos.length}</div>
                    {grupo.anexos.map(anexo => (
                        <div key={anexo.id} className="pr-doc">
                            <CIcon name="cil-file"/>
                            <div className="pr-doc-texto">
                                <button type="button" className="pr-link" onClick={() => baixarAnexo(anexo)}>{anexo.nome}</button>
                                <span>
                                    {anexo.enviado_pelo_cliente ? 'Cliente' : anexo.usuario} · {dataBr(anexo.data)} · {tamanhoArquivo(anexo.tamanho)}
                                    {anexo.item_id ? ` · ${onde.get(anexo.item_id) || ''}` : anexo.etapa_id ? ` · ${nomeEtapa(anexo.etapa_id) || ''}` : ''}
                                    {!anexo.visivel_cliente && ' · interno'}
                                </span>
                            </div>
                            {ativo && processo.pode_gerir && !anexo.item_id && (
                                <button type="button" className="pr-link perigo" onClick={() => remover(anexo)}>remover</button>
                            )}
                        </div>
                    ))}
                </section>
            ))}
        </div>
    );
};
